import { Component } from '@theme/component';

/**
 * Hero video component with auto-play/pause based on viewport visibility
 *
 * Automatically plays video when >50% visible and pauses when out of view
 * Only activates on videos with the autoplay attribute
 *
 * @extends {Component}
 */
class HeroVideo extends Component {
  #abortController = new AbortController();
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();

    // Only setup observer if autoplay attribute is present
    if (this.hasAttribute('autoplay')) {
      this.#setupIntersectionObserver();
    }
  }

  #setupIntersectionObserver() {
    const video = this.querySelector('video');
    if (!video) return;

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Play video when in view
            video.play().catch(() => {
              // Silently handle autoplay failures (e.g., browser policies)
            });
          } else {
            // Pause video when out of view
            video.pause();
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of video is visible
    );

    // Observe the video element directly, not the wrapper
    this.#intersectionObserver.observe(video);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#intersectionObserver?.disconnect();
  }
}

if (!customElements.get('hero-video')) {
  customElements.define('hero-video', HeroVideo);
}
